import { Checkbox, ListSubheader, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { To } from "react-router";

import { AreaCard, AreaCardVisitItem } from "~/modules/plannerV2/components/AreaCard";
import UserBadge from "~/modules/user/components/UserBadge";

import { UserGroup } from "../../hooks/useReviewPlanVisitGrouping";
import { ReviewPlanPendingAreaFragment } from "./fragments.generated";

export type ReviewPlanPendingArea = ReviewPlanPendingAreaFragment;

type ReviewPlanVisitsListProps = {
    userGroups: UserGroup[];
    visitHref: (plannerAreaId: string, plannerVisitId: string) => To;
    areaNameHref: (plannerAreaId: string) => To;
    selectedVisitIds: string[];
    onToggleVisit: (visitId: string) => void;
    onToggleUser: (userId: string | null) => void;
    isUserFullySelected: (userId: string | null) => boolean;
    isUserPartiallySelected: (userId: string | null) => boolean;
    getUserSelectedCount: (userId: string | null) => number;
};

export function ReviewPlanVisitsList({
    userGroups,
    visitHref,
    areaNameHref,
    selectedVisitIds,
    onToggleVisit,
    onToggleUser,
    isUserFullySelected,
    isUserPartiallySelected,
    getUserSelectedCount,
}: ReviewPlanVisitsListProps) {
    const { t } = useTranslation("plannerV2");
    return (
        <Stack rowGap={2}>
            {userGroups.map(group => (
                <Stack key={group.userId ?? "unknown"} rowGap={1}>
                    <ListSubheader sx={theme => ({ borderBottom: `1px solid ${theme.palette.divider}` })}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Checkbox
                                checked={isUserFullySelected(group.userId)}
                                indeterminate={isUserPartiallySelected(group.userId)}
                                onChange={() => onToggleUser(group.userId)}
                                sx={{ marginLeft: "-11px" }}
                            />
                            <UserBadge
                                profilePicture={group.stagedByUser.data?.profilePictureMedia?.url}
                                names={group.userName ? group.userName.split(" ") : []}
                                size={24}
                            />
                            <Typography variant="subtitle2">
                                {group.userId === null ? t("reviewPlanPanel.unknownUser") : group.userName} (
                                {t("reviewPlanPanel.userCount", {
                                    selected: getUserSelectedCount(group.userId),
                                    total: group.visitIds.length,
                                })}
                                ){group.isCurrentUser ? ` (${t("reviewPlanPanel.currentUserSuffix")})` : ""}
                            </Typography>
                        </Stack>
                    </ListSubheader>
                    {group.toBeCancelled.length > 0 && (
                        <Stack rowGap={1} paddingInline={2}>
                            <Typography flex={1} variant="overline" fontWeight="bold">
                                {t("reviewPlanPanel.toBeCancelled")}
                            </Typography>
                            {group.toBeCancelled.map(pendingArea => (
                                <AreaCard
                                    key={pendingArea.plannerArea.id}
                                    area={pendingArea.plannerArea}
                                    areaNameHref={areaNameHref}
                                >
                                    {pendingArea.visits.map(visit => (
                                        <AreaCardVisitItem
                                            key={visit.id}
                                            visit={visit}
                                            isToBeCancelled
                                            href={visitHref(pendingArea.plannerArea.id, visit.id)}
                                            showFieldworkers
                                            icon={
                                                <Checkbox
                                                    checked={selectedVisitIds.includes(visit.id)}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        onToggleVisit(visit.id);
                                                    }}
                                                    sx={{ alignSelf: "center", padding: 0 }}
                                                />
                                            }
                                        />
                                    ))}
                                </AreaCard>
                            ))}
                        </Stack>
                    )}
                    {group.toBePlanned.length > 0 && (
                        <Stack rowGap={1} paddingInline={2}>
                            <Typography flex={1} variant="overline" fontWeight="bold">
                                {t("reviewPlanPanel.toBePlanned")}
                            </Typography>
                            {group.toBePlanned.map(pendingArea => (
                                <AreaCard
                                    key={pendingArea.plannerArea.id}
                                    area={pendingArea.plannerArea}
                                    areaNameHref={areaNameHref}
                                >
                                    {pendingArea.visits.map(visit => (
                                        <AreaCardVisitItem
                                            key={visit.id}
                                            visit={visit}
                                            href={visitHref(pendingArea.plannerArea.id, visit.id)}
                                            showFieldworkers
                                            icon={
                                                <Checkbox
                                                    checked={selectedVisitIds.includes(visit.id)}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        onToggleVisit(visit.id);
                                                    }}
                                                    sx={{ alignSelf: "center", padding: 0 }}
                                                />
                                            }
                                        />
                                    ))}
                                </AreaCard>
                            ))}
                        </Stack>
                    )}
                </Stack>
            ))}
        </Stack>
    );
}
